//@ts-check
import * as d3 from "d3";

/**
 * @module Zoom
 */


/**
 * Mutliple Model Position
 * @param {Number} [percentage = 0.95]  Percentage 
 */
export function multipleModelPosition(percentage = 0.95) {
  let scope = this;
  let tempObjects = [];
  d3.select('#parent-' + scope.canvas.id).selectAll('#group').each(function () {
    let viewbox = d3.select(this.firstChild).node().getBBox();
    d3.select(this).attr('transform', 'translate(' + viewbox.x + ',' + viewbox.y + ')');
  });
  d3.select('#parent-' + scope.canvas.id).selectAll('g').each(function () {
    let object = d3.select(this).node().getBBox();
    tempObjects.push(object);
  });

  scope.zoomMultiple(tempObjects, percentage);
}

/**
 * Set the default view of loaded drawings in the added 2D Viewer
 */
export function zoomExtents() {
  let scope = this;
  if (scope.canvas.children.length != 0) {
    scope.multipleModelPosition();
  } else {
    console.log('Error : Drawing is not loaded in viewer.');
  }
}

/**
  * Focused to the single element
  * @param {any} group_ID Pass the element id from the loaded drawing in 2D Viewer
  * @returns {Object} Return success or error messages 
  */
export function zoomSelection(group_ID) {
  let scope = this;
  var object, tempObjects = [];
  if (group_ID != '') {
    if (scope.canvas.children.length != 0) {
      if (d3.select(scope.canvas).selectAll('#' + group_ID).size() > 1) {
        d3.select(scope.canvas).selectAll('#' + group_ID).each(function () {
          object = d3.select(this).node().getBBox();
          tempObjects.push(object);
        });
        scope.zoomMultiple(tempObjects);
      }
      else {
        let object = d3.select(scope.canvas).selectAll('#' + group_ID).node().getBBox();
        scope.zoomRegion(object.x, object.y, object.width, object.height);
      }
      return {
        "status": "success",
        "message": "Zoom selection API"
      };
    }
    else {
      return {
        'status': 'failed',
        'message': 'ERROR : Drawing is not loaded in viewer.'
      }
    }
  }
}

/**
 * Focused the multiple elements
 * @param {Array<string>} group_IDs Pass the array of element id from the loaded drawing in 2D Viewer
 * @returns {Object} Return success or error messages
 */
export function zoomToElements(group_IDs) {
  try {
    let scope = this;
    var object, tempObjects = [];
    if (group_IDs != null) {
      if (scope.canvas.children.length != 0) {
        for (let i = 0; i < group_IDs.length; i++) {
          let object = d3.select(scope.canvas).selectAll('#' + group_IDs[i]);
          if (object.size() > 1) {
            object.each(function () {
              tempObjects.push(d3.select(this).node().getBBox());
            });
          } else { tempObjects.push(object.node().getBBox()); }
        }
        if (group_IDs.length == 1) {
          scope.zoomSelection(group_IDs[0]);
        } else {
          const min_x = Math.min.apply(Math, tempObjects.map(function (element) { return element.x; }));
          const maxModel_x = Math.max.apply(Math, tempObjects.map(function (element) { return element.x + element.width; }));
          const max_x_index = tempObjects.findIndex((element) => element.x + element.width === maxModel_x);
          const max_x = tempObjects[max_x_index].x;
          const min_y = Math.min.apply(Math, tempObjects.map(function (element) { return element.y; }));
          const maxModel_y = Math.max.apply(Math, tempObjects.map(function (element) { return element.y + element.height; }));
          const max_y_index = tempObjects.findIndex((element) => element.y + element.height === maxModel_y);
          const max_y = tempObjects[max_y_index].y;
          const xIndexWidth = tempObjects.findIndex((element) => element.x === max_x);
          const yIndexheight = tempObjects.findIndex((element) => element.y === max_y);
          const width = tempObjects[xIndexWidth].width;
          const height = tempObjects[yIndexheight].height;
          scope.zoomRegion(min_x, max_x, min_y, max_y, width, height, undefined);
        }
      }
      if (group_IDs.length == 1) {
        scope.zoomSelection(group_IDs[0]);
      } else {
        scope.zoomMultiple(tempObjects);
      }
      return {
        "status": "success",
        "message": "Zoom to multiple selection API"
      };
    }
    else {
      return {
        'status': 'failed',
        'message': 'ERROR : Drawing is not loaded in viewer.'
      }
    }
  }
  catch (err) {
    return {
      'status': 'failed',
      'message': 'System Error:  '+ err.message
    }
  }
}

/**
 * Zoom multiple elements
 * @param {*} elments - Elements
 * @param {Number} [percentage = 0.95] 
 */
export function zoomMultiple(elments, percentage = 0.95) {
  let scope = this;

  const min_x = Math.min.apply(Math, elments.map(function (element) { return element.x; }));
  const max_x = Math.max.apply(Math, elments.map(function (element) { return element.x + element.width; }));

  const min_y = Math.min.apply(Math, elments.map(function (element) { return element.y; }));
  const max_y = Math.max.apply(Math, elments.map(function (element) { return element.y + element.height; }));

  const width = max_x - min_x;
  const height = max_y - min_y;

  scope.zoomRegion(min_x, min_y, width, height, percentage);
}

/**
 * Zoom Region
 * @param {Number} min_x - Min x
 * @param {Number} min_y - Min y
 * @param {Number} width - Width
 * @param {Number} height - Height
 * @param {Number} [percentage = 0.95] - Percentage
 */
export function zoomRegion(min_x, min_y, width, height, percentage = 0.95) {
  let scope = this;

  const canvasWidth = scope.canvas.offsetWidth;
  const canvasHeight = scope.canvas.offsetHeight;
  const scale = percentage / Math.max(width / canvasWidth, height / canvasHeight);

  d3.select(scope.canvas.children[0]).transition().duration(550)
    .call(scope.zoom.transform, d3.zoomIdentity
      .translate(canvasWidth / 2, canvasHeight / 2)
      .scale(scale)
      .translate(-(min_x + width / 2), -(min_y + height / 2))
    );

}


/**
 * Gets Current View
 * @returns {Object} Transform { x, y and k}
 */
export function getCurrentView() {
  let scope = this;
  // Get the current zoom transform
  let transform = d3.zoomTransform(d3.select(scope.canvas.children[0]).node());

  return {
    x: transform.x,
    y: transform.y,
    k: transform.k
  };
}


/**
 * Load Saved View
 * @param {Object} view Transform { x, y, k }
 * @returns {Object} Return success or error messages 
 */
export function loadSavedView(view) {
  let scope = this;
  if (view) {
    const { x, y, k } = view;

    // Apply the saved zoom transform
    d3.select(scope.canvas.children[0]).transition().duration(550)
      .call(scope.zoom.transform, d3.zoomIdentity.translate(x, y).scale(k));

    return {
      status: 'success',
      message: `View loaded successfully.`,
    };
  } else {
    return {
      status: 'failed',
      message: "View not valid",
    };
  }
}